import React, { useState } from "react";
import { Box, Typography, Collapse } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ExpandIcon } from "@icons/ExpandIcon";
import { CollapseIcon } from "@icons/CollapseIcon";
import { FAQIcon } from "@icons/FAQ";
import FAQs_background_image from "@assets/FAQs_background_image.png";
import { FAQ_DESC, FAQS } from "@constants/unicomply-keys";
const FAQSection = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
  padding: "100px 120px",
  position: "relative",
  overflow: "hidden",
  backgroundImage: `url(${FAQs_background_image})`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  [theme.breakpoints.down("lg")]: {
    padding: "80px 60px",
  },
  [theme.breakpoints.down("md")]: {
    padding: "60px 40px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "40px 16px",
  },
}));

const FAQBadge = styled(Box)({
  display: "inline-flex", // shrink to fit content
  alignItems: "center", // centers icon + text vertically
  justifyContent: "center",
  gap: 7,
  padding: "4px 8px", // balanced padding
  borderRadius: 14,
  border: "1px solid #0A829D",
  background: "linear-gradient(179deg, #FFF 83.87%, #F5F5F5 125.73%)",
  boxShadow: "0 1px 2px 0 rgba(82, 88, 102, 0.06)",
});

const FAQItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isOpen",
})<{ isOpen?: boolean }>(({ isOpen = false }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#ffffff",
  borderRadius: 12,
  border: isOpen ? "2px solid #0A829D" : "1px solid rgba(255, 255, 255, 0.50)",
  boxShadow: isOpen
    ? "0px 1px 1px rgba(131, 115, 238, 0.2), 0px 3px 3px rgba(131, 115, 238, 0.09)"
    : "none",
  transition: "all 0.3s ease",
  marginBottom: 15,
  padding: 5,
}));

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}

function FAQItemComponent({
  question,
  answer,
  isOpen,
  onToggle,
}: FAQItemProps) {
  return (
    <FAQItem isOpen={isOpen}>
      {/* <FAQHeader onClick={onToggle}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: 20,
              lineHeight: 1.4,
              flex: 1,
              color: isOpen ? "#35d7e4" : "#1f2937",
              fontFamily: "Rethink Sans",
              fontStyle: "normal",
              textShadow: "4px 4px 4px rgba(0, 0, 0, 0.00)",
            }}
          >
            {question}
          </Typography>

          <Box
            sx={{
              width: 24,
              height: 24,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "& svg": {
                width: 20,
                height: 20,
                fill: isOpen ? "#35d7e4" : "#6b7280",
              },
            }}
          >
            {isOpen ? <CollapseIcon /> : <ExpandIcon />}
          </Box>
        </Box>
      </FAQHeader> */}
      <Box
        onClick={onToggle}
        sx={{
          width: "95%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          mb: 1,
          p: 1,
        }}
      >
        <Box>
          {" "}
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: 20,
              lineHeight: 1.4,
              flex: 1,
              color: isOpen ? "#35d7e4" : "#1f2937",
              fontStyle: "normal",
              textShadow: "4px 4px 4px rgba(0, 0, 0, 0.00)",
            }}
          >
            {question}
          </Typography>
        </Box>
        <Box>{isOpen ? <CollapseIcon /> : <ExpandIcon />}</Box>
      </Box>

      <Collapse in={isOpen}>
        <Box
          sx={{
            width: "95%",
            mb: 1,
            p: 1,
          }}
        >
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: 15,
              lineHeight: 1.6,
              color: "var(--text-sub-500, #525866)",
              fontStyle: "normal",
            }}
          >
            {answer}
          </Typography>
        </Box>
      </Collapse>
    </FAQItem>
  );
}

export default function UniComply_FAQ() {
  // theme not used here; remove to satisfy linter
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqData = [
    {
      question: "What is UniComply and who is it for?",
      answer:
        "UniComply is an AI-powered compliance hub for healthcare organizations such as Rural Health Clinics (RHCs), Federally Qualified Health Centers (FQHCs), Critical Access Hospitals, and larger health systems. It centralizes compliance requirements, policies, potential penalties, and training in one place, giving compliance managers, Chief Operating Officers (COOs), and administrators a clear and actionable view of what is required for their facility type.",
    },
    {
      question: "Does UniComply support both federal and state regulations?",
      answer:
        "Yes. UniComply aggregates requirements from federal agencies (CMS, HHS, OSHA, etc.) and state health departments, giving each facility a single point of truth for their obligations.",
    },
    {
      question: "Can I upload my own policies and documents?",
      answer:
        "Yes. Facilities can upload policies, contracts, or survey reports to generate compliance insights and keep everything in a centralized repository. Pre-built customizable templates are also available for emergency preparedness, policies, and biennial evaluations.",
    },
    {
      question: "How often is UniComply updated?",
      answer:
        "UniComply continuously refreshes its content from official sources. If CMS or OSHA publishes new rules, the updates are reflected in the platform’s analysis reports. A Discover section highlights the latest compliance news with AI-generated summaries.",
    },
    {
      question: "What kind of customer support is provided?",
      answer:
        "UniComply Enterprise package offers dedicated customer support. Services include onboarding, remote assistance, regular reviews, and updates on regulatory changes. Support is available via phone and email for all customers.",
    },
  ];

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <FAQSection id="faqs">
      {/* Background blur element */}
      <Box
        sx={{
          position: "absolute",
          left: -448,
          top: -940,
          pointerEvents: "none",
        }}
      >
        <Box sx={{}}>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              background:
                "linear-gradient(135deg, rgba(195, 244, 248, 0.1) 0%, transparent 100%)",
              borderRadius: "50%",
              filter: "blur(120px)",
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          maxWidth: 1200,
          margin: "0 auto",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {/* FAQ Badge */}
          <FAQBadge>
            <Box
              sx={{
                width: 13,
                height: 13,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FAQIcon />
            </Box>
            <Typography
              sx={{
                color: "var(--Brand-Colors-color-brand-primary, #0A829D)",
                fontSize: "12px",
                fontWeight: 500,
                lineHeight: "16px", // keep consistent with text size
                letterSpacing: "-0.24px",
              }}
            >
              {FAQS}
            </Typography>
          </FAQBadge>

          {/* Title */}
          <Typography
            sx={{
              color: "#111127",
              textAlign: "center",
              //   fontFamily: "Rethink Sans",
              fontSize: "48px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "114%", // or "54.72px"
              letterSpacing: "-1px",
            }}
          >
            {FAQ_DESC}
          </Typography>
        </Box>

        {/* FAQ Items */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            maxWidth: 916,
          }}
        >
          {faqData.map((faq, index) => (
            <FAQItemComponent
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onToggle={() => handleToggle(index)}
            />
          ))}
        </Box>
      </Box>
    </FAQSection>
  );
}
